import { useAuth } from "@/components/features/auth/hooks/use-auth";
import {
  Account,
  useUserOrganizations,
} from "@/components/features/users/hooks/use-user-orgs";
import { orgClient, userClient } from "@/lib/connect.client";
import { getCoverImage } from "@/lib/image/usage";
import { routes } from "@/lib/routes";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@workspace/ui/components/command";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@workspace/ui/components/dialog";
import { LoadingSpinner } from "@workspace/ui/components/loading-spinner";
import { map } from "lodash";
import { ArrowUpRight, Check, Plus } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSwitchAccount } from "./features/users/hooks/use-switch-account";
import { useUser } from "./features/users/hooks/use-user";

const getImageUrl = async (id: string, slug?: string) => {
  if (slug) {
    const orgResponse = await orgClient.get({ slug });
    const orgImages = orgResponse.org?.images;
    if (orgImages) {
      const coverImage = getCoverImage(orgImages);
      return coverImage?.url;
    }
  } else {
    const userResponse = await userClient.get({
      identifier: { case: "userId", value: id },
    });
    if (!userResponse.userProfile) return undefined;
    const userImages = userResponse.userProfile.images;
    if (userImages) {
      const coverImage = getCoverImage(userImages);
      return coverImage?.url ?? undefined;
    }
  }
  return undefined;
};

function AccountItem({
  id,
  label,
  onClick,
  loadingContextForId,
  loading,
  selected,
}: {
  id: string;
  label: string;
  onClick: (id: string) => void;
  loadingContextForId?: string;
  loading: boolean;
  selected: boolean;
}) {
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    const getImage = async () => {
      const org = await orgClient.getById({ id });
      if (!org?.org?.slug) return;

      const imageUrl = await getImageUrl(id, org.org?.slug);
      if (!imageUrl) return;

      setImageUrl(imageUrl);
    };
    getImage();
  }, [id]);

  return (
    <CommandItem
      className="flex justify-between"
      key={id}
      value={id}
      onSelect={(currentValue) => onClick(currentValue)}
    >
      <div className="flex items-center gap-4 font-medium">
        <Avatar>
          <AvatarImage src={imageUrl} alt={label} />
          <AvatarFallback>{label[0]?.toUpperCase() || "P"}</AvatarFallback>
        </Avatar>

        {label}
      </div>
      {selected && !loading && (
        <div className="bg-brand-50 rounded-full p-1">
          <Check className="text-brand-700 h-4 w-4" />
        </div>
      )}
      {loading && loadingContextForId === id && <LoadingSpinner />}
    </CommandItem>
  );
}

function PersonalAccountItem({
  id,
  label,
  onClick,
  loadingContextForId,
  loading,
  selected,
}: {
  id: string;
  label: string;
  onClick: (id: string) => void;
  loadingContextForId?: string;
  loading: boolean;
  selected: boolean;
}) {
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    const getImage = async () => {
      const imageUrl = await getImageUrl(id);
      if (!imageUrl) return;
      setImageUrl(imageUrl);
    };
    getImage();
  }, [id]);

  const firstCharacter = label[0] === "@" ? label[1] : label[0];
  return (
    <CommandItem
      className="flex justify-between"
      key={id}
      value={id}
      onSelect={(currentValue) => onClick(currentValue)}
    >
      <div className="flex items-center gap-4 font-medium">
        <Avatar>
          <AvatarImage src={imageUrl} alt={label} />
          <AvatarFallback className="bg-brand-100">
            {firstCharacter?.toUpperCase() || "P"}
          </AvatarFallback>
        </Avatar>

        {label}
      </div>
      {selected && !loading && (
        <div className="bg-brand-50 rounded-full p-1">
          <Check className="text-brand-700 h-4 w-4" />
        </div>
      )}
      {loading && loadingContextForId === id && <LoadingSpinner />}
    </CommandItem>
  );
}

export function AccountSwitcher({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingContextForId, setLoadingContextForId] = useState<string>();
  const { data: userData } = useUser(user?.uid || "", Boolean(user));
  const { mutateAsync } = useSwitchAccount();
  const activeOrg = userData?.userProfile?.activeOrg;
  const router = useRouter();

  const { data: accounts = [], isLoading: isLoadingAccounts } =
    useUserOrganizations();

  const accountSwitchHandler = async (accountId: string) => {
    setLoading(true);
    setLoadingContextForId(accountId);
    try {
      await mutateAsync({
        orgId: accountId !== user?.uid ? accountId : undefined,
      });
      onOpenChange(false);
      window.location.reload();
      toast.success("Account switched successfully", {
        description: `You are now using the selected account: ${accountId === user?.uid ? "personal" : Object.values(accounts).find((account) => account.id === accountId)?.label}`,
      });
    } catch {
      toast.error("Error switching account", {
        description: "Please try again",
      });
    } finally {
      setLoading(false);
      setLoadingContextForId(undefined);
    }
  };

  if (!user) return null;

  const renderAccounts = () => {
    return map(accounts, ({ id, label }: Account) => (
      <AccountItem
        key={id}
        id={id}
        label={label}
        loading={loading || isLoadingAccounts}
        loadingContextForId={loadingContextForId}
        selected={activeOrg?.id === id || (id === user?.uid && !activeOrg)}
        onClick={accountSwitchHandler}
      />
    ));
  };

  const username = userData?.userProfile?.username
    ? `@${userData.userProfile.username}`
    : user.displayName || user.email || "Personal";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-md sm:max-w-md" glassOverlay>
        <DialogHeader>
          <DialogTitle>Choose Account</DialogTitle>
        </DialogHeader>

        <Command className="bg-white/40">
          {/* <CommandInput placeholder="Search account..." /> */}
          <CommandList>
            <CommandEmpty>No account found.</CommandEmpty>

            <CommandGroup>
              <PersonalAccountItem
                id={user.uid}
                label={username || user.displayName || user.email || "Personal"}
                onClick={accountSwitchHandler}
                loading={loading || isLoadingAccounts}
                selected={!activeOrg}
              />
              {renderAccounts()}
            </CommandGroup>
          </CommandList>
        </Command>

        <DialogFooter>
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                onOpenChange(false);
                // Navigate to business creation with upgrade context
                router.push(routes.auth.org.create);
              }}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create Business Account
                </div>
                <ArrowUpRight className="text-muted-foreground h-4 w-4" />
              </div>
            </Button>

            {/* Upgrade context info */}
            <p className="text-muted-foreground text-center text-xs">
              Upgrade from consumer to business account to create events, offer
              services, and grow your customer base.
            </p>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
